import { ReactElement, useEffect } from 'react';
import {
  Stack,
  TextField,
  Typography,
  Avatar,
  FormControlLabel,
  Switch,
  createFilterOptions,
} from '@mui/material';

import { PlanFeaturesTypes, Roles } from '~globals/types/enums';
import {
  AccessFeature,
  Subscription,
  CustomAutocomplete,
  UserInfo,
} from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import { COLLECT_STATE_OPTIONS } from '~constants/ordersStatesOptions';

import { useOrdersFiltersMenuV2Context } from '../../Context';

import { OrdersFiltersMenuV2Inputs } from '../../styles';

const OrdersFiltersMenuV2VariantCollect = (): ReactElement => {
  const {
    filters: { collect: filtersCollect },
    handleChangeInputDateFilter,
    selectsData,
  } = useOrdersFiltersMenuV2Context();

  useEffect(() => {
    if (filtersCollect.activeFilters.collectState?.length) {
      filtersCollect.autocompletesRefs.collectState.current?.setValue(
        filtersCollect.activeFilters.collectState,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersCollect.activeFilters.collectState]);

  useEffect(() => {
    if (filtersCollect.activeFilters.giverCompany) {
      filtersCollect.autocompletesRefs.giverCompany.current?.setValue(
        filtersCollect.activeFilters.giverCompany,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersCollect.activeFilters.giverCompany]);

  useEffect(() => {
    if (filtersCollect.activeFilters.assignedUser) {
      filtersCollect.autocompletesRefs.assignedUser.current?.setValue(
        filtersCollect.activeFilters.assignedUser,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersCollect.activeFilters.assignedUser]);

  return (
    <OrdersFiltersMenuV2Inputs>
      <Stack spacing={0.5}>
        <Stack spacing={0.5} direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            &#8288;Fecha de vencimiento
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={
                  filtersCollect.filters.includeWithoutExpirationDate ?? false
                }
                onChange={(event) =>
                  filtersCollect.onChangeFilterInput(
                    'includeWithoutExpirationDate',
                    event.target.checked,
                  )
                }
                size="small"
              />
            }
            label="Sin fecha de vencimiento"
            componentsProps={{
              typography: {
                variant: 'caption',
                fontSize: 10,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersCollect.filters.expirationDateTimeFrom}
            onChange={handleChangeInputDateFilter(filtersCollect)(
              'expirationDateTimeFrom',
            )}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersCollect.filters.expirationDateTimeTo}
            onChange={handleChangeInputDateFilter(filtersCollect)(
              'expirationDateTimeTo',
            )}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Fecha de creación
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersCollect.filters.dateFrom ?? null}
            onChange={handleChangeInputDateFilter(filtersCollect)('dateFrom')}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersCollect.filters.dateTo ?? null}
            onChange={handleChangeInputDateFilter(filtersCollect)('dateTo')}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <CustomAutocomplete
        innerRef={filtersCollect.autocompletesRefs.collectState}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={COLLECT_STATE_OPTIONS}
        label="Estados"
        name="collectState"
        onChange={(_e, vals) => {
          filtersCollect.onChangeFilterInput('collectState', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <CustomAutocomplete
        innerRef={filtersCollect.autocompletesRefs.giverCompany}
        options={selectsData.giverCompany.list}
        loading={selectsData.giverCompany.loading}
        label="Dador de carga"
        name="giverCompany"
        onChange={(_e, val) => {
          filtersCollect.onChangeFilterInput('giverCompany', val);
        }}
        getOptionLabel={(option) => option.name}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <UserInfo name={option.name} urlAvatar={option.logoFile?.url} />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={val.logoFile?.url}
              alt={val?.name}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) => option.name.replace(',', ''),
        })}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersCollect.autocompletesRefs.assignedUser}
        options={selectsData.assignedUser.list}
        loading={selectsData.assignedUser.loading}
        label="Usuario asignado"
        name="assignedUser"
        onChange={(_e, val) => {
          filtersCollect.onChangeFilterInput('assignedUser', val);
        }}
        fullWidth
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.userId}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <UserInfo
              name={`${option.lastName}, ${option.firstName}`}
              urlAvatar={option.avatarUrl || undefined}
            />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={getDefaultAvatar(val.avatarUrl || undefined)}
              alt={`${val.lastName}, ${val.firstName}`}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        groupBy={(option) => {
          if (option.roleId === Roles.Drive) {
            return 'Choferes';
          }

          return 'Usuarios';
        }}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) =>
            `${option.lastName}, ${option.firstName}`.replace(',', ''),
        })}
      />
    </OrdersFiltersMenuV2Inputs>
  );
};

export default OrdersFiltersMenuV2VariantCollect;
